import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MdFileUpload, MdArrowBack } from 'react-icons/md';

import {
  Title,
  Button,
  Breadcrumbs,
  WrapperFlex,
  LogsFileErrorStorage,
} from '@unimed-fesp/components';

// #region Import Componentes
import FadeIn from '../../components/atoms/FadeIn';
import TableDocumentos from '../../components/organisms/TableDocumentos';
import ModalMotivoRecusa from '../../components/organisms/ModalMotivoRecusa';
import ImportarDocumentos from '../../components/organisms/ImportarDocumentos';
import ModalDetalheDocumento from '../../components/organisms/ModalDetalheDocumento';
// #endregion

import { toBase64 } from '../../utils/array';
import { isAdmin } from '../../utils/userRole';
import StatusListaAPIEnum from '../../enums/StatusListaAPIEnum';

import { Creators as DocumentoActions } from '../../store/ducks/documento';
import history from '../../services/history';

import { Container } from './styles';
import theme from '../../styles/theme';

function DetalheDocumentosInativos({ match }) {
  const dispatch = useDispatch();
  const { idLista } = match.params;
  const {
    documentoPreview,
    documentosImportacao,
    documentosBeneficiarios,
  } = useSelector((state) => state.documento);
  const { usuario } = useSelector((state) => state.auth);

  const [isOpenModalImportDocs, setIsOpenModalImportDocs] = useState(false);
  const [isOpenModalPreviewDoc, setIsOpenModalPreviewDoc] = useState(false);
  const [isOpenModalMotivoRecusa, setIsOpenModalMotivoRecusa] = useState(false);

  const [tipoDocumentoSelected, setTipoDocumentoSelected] = useState('');
  const [statusDocumentoSelected, setStatusDocumentoSelected] = useState('');
  const [idDocumentoSelected, setIdDocumentoSelected] = useState(0);

  const handleAceitarRecusarDoc = useCallback(
    (params) => {
      dispatch(
        DocumentoActions.postAtualizarDocumentoConferenciaRequest(
          idLista,
          params,
          () =>
            dispatch(DocumentoActions.getDocumentosBeneficiarioRequest(idLista))
        )
      );
      setIsOpenModalMotivoRecusa(false);
      setIsOpenModalPreviewDoc(false);
    },
    [dispatch, idLista]
  );

  const handleAceiteRecusaAdm = useCallback(
    (aceito) => {
      if (aceito) {
        handleAceitarRecusarDoc({
          idArquivo: idDocumentoSelected,
          statusConferencia: 'Aceito',
        });
      } else {
        setIsOpenModalPreviewDoc(false);
        setIsOpenModalMotivoRecusa(true);
      }
    },
    [handleAceitarRecusarDoc, idDocumentoSelected]
  );

  const handleModalDetalheDocumento = useCallback(
    (
      idDocumento,
      _idLista,
      _id,
      _idMovimentacao,
      tipoDocumento,
      statusDocumento
    ) => {
      dispatch(
        DocumentoActions.getDocumentoPreviewRequest(idLista, idDocumento)
      );
      setIsOpenModalPreviewDoc(true);
      setIdDocumentoSelected(idDocumento);
      setTipoDocumentoSelected(tipoDocumento);
      setStatusDocumentoSelected(statusDocumento);
    },
    [dispatch, idLista]
  );

  const onCloseModalPreviewDoc = useCallback(() => {
    setIsOpenModalPreviewDoc((oldValue) => !oldValue);
  }, []);

  const redirectToDetalheLista = useCallback((id) => {
    history.push(`/novo/listainativo/${id}/detalhe`);
  }, []);

  const onCancelarImport = useCallback(() => {
    setIsOpenModalImportDocs(false);
    dispatch(DocumentoActions.postDocumentosImportacaoRenew());
    dispatch(DocumentoActions.getDocumentosBeneficiarioRequest(idLista));
  }, [dispatch, idLista]);

  const logError = [
    ...documentosImportacao.files
      .filter((file) => file.error)
      .map((file) => ({
        hasError: true,
        fileName: file.name,
        base64: toBase64(file.message),
      })),
  ];

  useEffect(() => {
    dispatch(DocumentoActions.getDocumentosBeneficiarioRequest(idLista));
  }, [dispatch, idLista]);

  const IS_ADM = isAdmin(usuario.role) || false;
  const AGUARDANDO_DOCUMENTOS =
    documentosBeneficiarios.response.lista?.idStatusLista ===
    StatusListaAPIEnum.returnId.AguardandoDocumentos;
  const ERRO_DE_PROCESSAMENTO =
    documentosBeneficiarios.response.lista?.idStatusLista ===
    StatusListaAPIEnum.returnId.ErrodeProcessamento;

  return (
    <Container>
      <FadeIn>
        <WrapperFlex justifyStart alignStart column>
          <WrapperFlex fullWidth justifySpaceBetween alignStart>
            <Title
              text={`documentos da lista: ${
                documentosBeneficiarios?.response?.lista?.nmLista || ''
              }`}
              fontSize={34}
              color={theme.titleGray}
              marginBottom={8}
            />
            <WrapperFlex gap={20} flexWrap>
              <Button
                tipo="secondary"
                font="16px unimed_slabregular"
                onClick={() => redirectToDetalheLista(idLista)}
              >
                <MdArrowBack
                  color={theme.greenDefault}
                  style={{ marginRight: 5 }}
                />
                voltar
              </Button>

              {(AGUARDANDO_DOCUMENTOS || ERRO_DE_PROCESSAMENTO) && !IS_ADM && (
                <Button
                  font="16px unimed_slabregular"
                  onClick={() => setIsOpenModalImportDocs(true)}
                >
                  <MdFileUpload
                    color={theme.white}
                    style={{ marginRight: 5 }}
                  />
                  importar docs
                </Button>
              )}
            </WrapperFlex>
          </WrapperFlex>
          <Breadcrumbs
            crumbs={[
              { path: 'novo' },
              { path: 'listas', onNavigate: () => history.push('/novo/lista') },
              {
                path: documentosBeneficiarios?.response?.lista?.nmLista || '',
                onNavigate: () => redirectToDetalheLista(idLista),
              },
              {
                path: 'documentos',
                onNavigate: () =>
                  history.push(`/novo/lista/${idLista}/documentos`),
              },
            ]}
          />
        </WrapperFlex>
      </FadeIn>
      <LogsFileErrorStorage logs={logError}>
        <ImportarDocumentos
          idLista={idLista}
          isOpen={isOpenModalImportDocs}
          onCloseModal={onCancelarImport}
          documentosImportacao={documentosImportacao}
        />
      </LogsFileErrorStorage>
      <WrapperFlex marginTop={25} justifyStart alignStart row>
        <TableDocumentos
          idLista={idLista}
          loading={documentosBeneficiarios.loading}
          headers={documentosBeneficiarios.response.header}
          beneficiarios={[documentosBeneficiarios.response.beneficiario]}
          handleModalDetalheDocumento={handleModalDetalheDocumento}
          hasPagination={false}
        />
      </WrapperFlex>
      <ModalDetalheDocumento
        isOpen={isOpenModalPreviewDoc}
        tipoDocumento={tipoDocumentoSelected}
        documentoPreview={documentoPreview}
        statusDocumento={statusDocumentoSelected}
        onRecusar={() => handleAceiteRecusaAdm(false)}
        onAceitar={() => handleAceiteRecusaAdm(true)}
        idStatusLista={documentosBeneficiarios.response.lista?.idStatusLista}
        onCloseModal={onCloseModalPreviewDoc}
        reenviarDocumento={() => {
          onCloseModalPreviewDoc();
          setIsOpenModalImportDocs(true);
        }}
        esconderBtnReenviar={
          !((AGUARDANDO_DOCUMENTOS || ERRO_DE_PROCESSAMENTO) && !IS_ADM)
        }
      />
      <ModalMotivoRecusa
        isOpen={isOpenModalMotivoRecusa}
        idArquivo={idDocumentoSelected}
        onClick={() => {
          setIsOpenModalMotivoRecusa(false);
          setIsOpenModalPreviewDoc(true);
        }}
        onClickEnviar={handleAceitarRecusarDoc}
      />
    </Container>
  );
}

export default DetalheDocumentosInativos;
